import { useState, useEffect } from "react";
import { T } from "../lib/i18n";
import { friendlyError } from "../lib/helpers";
import { serverNow } from "../lib/net";
import { dbLoadSession, dbJoinGroup, dbLoadActiveGroups } from "../lib/db";
import { AppHeader } from "../components/common";

// ─────────────────────────────────────────
//  GROUP JOIN — with existing group option
// ─────────────────────────────────────────
function GroupJoin({ sessionId, onJoin, onBack, onHelp, lang, setLang, theme, setTheme, G, css }) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [err, setErr] = useState("");
  const [activeGroups, setActiveGroups] = useState([]);
  const [mode, setMode] = useState("new"); // "new" | "existing"
  const [enteredCode, setEnteredCode] = useState("");
  const [codeErr, setCodeErr] = useState("");
  const t = T[lang];

  const [joining, setJoining] = useState(false);
  // Group codes never reach the client anymore — the server sets has_group_codes
  const hasCodes = session?.config?.has_group_codes
    || (session?.config?.group_codes && Object.keys(session.config.group_codes).length > 0);
  const isPredefined = session?.config?.predefined_groups?.length > 0;
  const needsCode = hasCodes && isPredefined && name;

  useEffect(() => {
    dbLoadSession(sessionId).then(async s => {
      if (!s) { setErr(t.sessionNotFound); setLoading(false); return; }
      setSession(s);
      const groups = await dbLoadActiveGroups(sessionId);
      setActiveGroups(groups);
      setLoading(false);
    });
  }, [sessionId]);

  async function join() {
    if (!name.trim()) { setErr(lang === 'sv' ? 'Välj din grupp.' : 'Please select your group.'); return; }
    if (!session || joining) return;

    // Quick local check using server-synced clock (server enforces it too)
    if (session.availableAt) {
      const availableTime = new Date(session.availableAt);
      if (serverNow() < availableTime.getTime()) {
        setErr(`${t.sessionNotYetAvailable} ${availableTime.toLocaleString(lang === 'sv' ? 'sv-SE' : 'en-US')}`);
        return;
      }
    }

    // Group code is validated server-side by join_group
    setJoining(true);
    try {
      const res = await dbJoinGroup(sessionId, name.trim(), needsCode ? enteredCode.trim() : null);
      if (res.error === "bad_group_code") { setCodeErr(t.groupCodeError); return; }
      if (res.error === "locked") { setErr(t.sessionLockedJoin); return; }
      if (res.error === "not_available") {
        const at = res.availableAt ? new Date(res.availableAt).toLocaleString(lang === 'sv' ? 'sv-SE' : 'en-US') : "";
        setErr(`${t.sessionNotYetAvailable} ${at}`);
        return;
      }
      if (res.error) { setErr(friendlyError({ message: res.error }, lang)); return; }
      onJoin(session, name.trim());
    } catch (e) {
      setErr(friendlyError(e, lang));
    } finally {
      setJoining(false);
    }
  }

  return (
    <div style={css.page}>
      <AppHeader lang={lang} setLang={setLang} onHome={onBack} theme={theme} setTheme={setTheme} onHelp={onHelp} G={G} css={css}>
        <button style={{ background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 14 }} onClick={onBack}>{t.back}</button>
      </AppHeader>
      <div style={{ display: "flex", justifyContent: "center", padding: "40px 20px" }}>
        <div style={css.card}>
          <h2 style={{ ...css.heading, fontSize: 28, marginBottom: 4 }}>{session?.sessionTitle || t.joinSession}</h2>
          <p style={{ color: G.muted, fontSize: 14, marginBottom: 28 }}>Session: <span style={{ fontFamily: "'DM Mono',monospace", color: G.gold }}>{sessionId}</span></p>
          {loading && <div style={{ color: G.muted, textAlign: "center", padding: "20px 0" }}>{t.loading}</div>}
          {!loading && session?.availableAt && new Date(session.availableAt) > new Date() && (
            <div style={{ background: G.gold + "18", border: `2px solid ${G.gold}44`, borderRadius: 12, padding: "18px 20px", textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 18, color: G.gold, fontWeight: 600, marginBottom: 8 }}>🕐 {t.scheduledFor}</div>
              <div style={{ fontSize: 16, color: G.text, lineHeight: 1.6 }}>{new Date(session.availableAt).toLocaleString(lang === 'sv' ? 'sv-SE' : 'en-US')}</div>
            </div>
          )}
          {!loading && session?.sessionLocked && (
            <div style={{ background: G.red + "18", border: `2px solid ${G.red}44`, borderRadius: 12, padding: "18px 20px", textAlign: "center" }}>
              <div style={{ fontSize: 18, color: G.red, fontWeight: 600, marginBottom: 8 }}>🔒 {t.sessionLocked}</div>
              <div style={{ fontSize: 14, color: G.text, lineHeight: 1.6 }}>{t.sessionLockedJoin}</div>
            </div>
          )}
          {!loading && session && !session.sessionLocked && (
            <>
              {/* If teacher pre-registered groups, show them as primary option */}
              {session.config?.predefined_groups?.length > 0 ? (
                <div style={{ marginBottom: 24 }}>
                  <label style={css.label}>{t.groupName}</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                    {session.config.predefined_groups.map(g => {
                      const active = activeGroups.find(a => a.group_name === g);
                      return (
                        <button key={g} onClick={() => { setName(g); setErr(""); setEnteredCode(""); setCodeErr(""); }}
                          style={{ background: name === g ? G.gold + "22" : G.bg, border: `2px solid ${name === g ? G.gold : G.border}`, borderRadius: 12, padding: "12px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "all 0.15s" }}>
                          <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: G.text, fontWeight: 500 }}>{g}</span>
                          {active && <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, color: active.in_progress ? G.gold : G.accent }}>● {active.in_progress ? (lang === "sv" ? "pågår" : "in progress") : (lang === "sv" ? "klar" : "done")}</span>}
                        </button>
                      );
                    })}
                  </div>
                  {/* Code input — shown once a group is selected and session uses codes */}
                  {name && hasCodes && (
                    <div style={{ marginTop: 4 }}>
                      <label style={css.label}>{t.groupCodeLabel}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={4}
                        autoFocus
                        style={{ ...css.input, fontFamily: "'DM Mono',monospace", fontSize: 24, letterSpacing: "0.25em", textAlign: "center", padding: "12px 14px" }}
                        placeholder="····"
                        value={enteredCode}
                        onChange={e => { setEnteredCode(e.target.value.replace(/\D/g, "").slice(0, 4)); setCodeErr(""); }}
                        onKeyDown={e => e.key === "Enter" && join()}
                      />
                      {codeErr && <div style={{ color: G.red, fontSize: 13, marginTop: 6 }}>{codeErr}</div>}
                      {!codeErr && <div style={{ color: G.muted, fontSize: 13, marginTop: 6 }}>{t.groupCodeHint}</div>}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Tab selector */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 24 }}>
                    {[{ id: "new", label: t.createNewGroup }, { id: "existing", label: t.joinExistingGroup }].map(({ id, label }) => (
                      <button key={id} onClick={() => { setMode(id); setName(""); setErr(""); }}
                        style={{ background: mode === id ? G.gold : G.surface2, color: mode === id ? G.bg : G.muted, border: `1px solid ${mode === id ? G.gold : G.border}`, borderRadius: 8, padding: "9px 12px", fontSize: 14, fontWeight: mode === id ? 600 : 400, cursor: "pointer", transition: "all 0.2s" }}>
                        {label}
                      </button>
                    ))}
                  </div>

                  {mode === "new" && (
                    <div style={{ marginBottom: 24 }}>
                      <label style={css.label}>{t.groupName}</label>
                      <input style={css.input} placeholder={t.groupPlaceholder} value={name} onChange={e => { setName(e.target.value); setErr(""); }} onKeyDown={e => e.key === "Enter" && join()} autoFocus />
                    </div>
                  )}

                  {mode === "existing" && (
                    <div style={{ marginBottom: 24 }}>
                      {activeGroups.length === 0
                        ? <div style={{ color: G.muted, fontSize: 14, textAlign: "center", padding: "16px 0" }}>{lang === "sv" ? "Inga aktiva grupper ännu." : "No active groups yet."}</div>
                        : <>
                          <label style={css.label}>{t.existingGroups}</label>
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {activeGroups.map(g => (
                              <button key={g.group_name} onClick={() => setName(g.group_name)}
                                style={{ background: name === g.group_name ? G.gold + "22" : G.bg, border: `2px solid ${name === g.group_name ? G.gold : G.border}`, borderRadius: 12, padding: "12px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "all 0.15s" }}>
                                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: G.text, fontWeight: 500 }}>{g.group_name}</span>
                                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, color: g.in_progress ? G.gold : G.accent }}>{g.in_progress ? (lang === "sv" ? "● Pågår" : "● In progress") : (lang === "sv" ? "● Klar" : "● Done")}</span>
                              </button>
                            ))}
                          </div>
                        </>
                      }
                    </div>
                  )}
                </>
              )}

              {err && <div style={{ color: G.red, fontSize: 14, marginBottom: 14 }}>{err}</div>}
              <button style={{ ...css.btnPrimary, width: "100%", opacity: (joining || !name.trim() || (needsCode && enteredCode.length < 4)) ? 0.45 : 1 }} onClick={join} disabled={joining || !name.trim() || (needsCode && enteredCode.length < 4)}>{joining ? t.loading : t.start}</button>
            </>
          )}
          {!loading && !session && <div style={{ color: G.red, fontSize: 14, textAlign: "center" }}>{err}</div>}
          <button style={css.btnSecondary} onClick={onBack}>{t.back}</button>
        </div>
      </div>
    </div>
  );
}

export { GroupJoin };
